import { makeAutoObservable, runInAction } from 'mobx';
import type { RootStore } from '../root-store';
import {
    CreateCriticalNotificationDto,
    CriticalNotificationDto,
    CriticalNotificationSeverityEnum,
    UpdateCriticalNotificationDto,
} from '../../../defs/api';

export type CriticalNotificationDialogMode = 'create' | 'update';

export class AdminCriticalNotificationStore {
    rootStore: RootStore;

    criticalNotifications: CriticalNotificationDto[] = [];

    editedCriticalNotification: CriticalNotificationDto | null = null;

    dialogMode: CriticalNotificationDialogMode = 'create';

    dialogOpened = false;

    loading = false;

    deleteInProgress = false;

    createOrUpdateErrorMessage: string | null = null;

    constructor(rootStore: RootStore) {
        this.rootStore = rootStore;
        makeAutoObservable(this, {
            rootStore: false,
        });
    }

    async fetchCriticalNotifications(): Promise<void> {
        try {
            this.setLoading(true);
            const criticalNotifications =
                await this.rootStore.apiClient.adminCriticalNotificationsController.criticalNotificationAdminControllerGetAdminList();

            runInAction(() => {
                this.criticalNotifications = criticalNotifications;
            });
        } catch (e) {
            // eslint-disable-next-line no-console
            console.error(e);
            this.rootStore.alertStore.setErrorMessageByStatus(e.message);
        } finally {
            this.setLoading(false);
        }
    }

    async create(input: CreateCriticalNotificationDto): Promise<void> {
        this.createOrUpdateErrorMessage = null;
        try {
            this.setLoading(true);
            await this.rootStore.apiClient.adminCriticalNotificationsController.criticalNotificationAdminControllerCreate(
                {
                    createCriticalNotificationDto: input,
                },
            );
            await this.fetchCriticalNotifications();
            this.closeDialog();
        } catch (e) {
            runInAction(() => {
                this.createOrUpdateErrorMessage = e.message;
            });
            // eslint-disable-next-line no-console
            console.error(e);
        } finally {
            this.setLoading(false);
        }
    }

    async update(input: UpdateCriticalNotificationDto): Promise<void> {
        if (!this.editedCriticalNotification) return;

        this.createOrUpdateErrorMessage = null;
        try {
            this.setLoading(true);
            await this.rootStore.apiClient.adminCriticalNotificationsController.criticalNotificationAdminControllerUpdate(
                {
                    id: this.editedCriticalNotification.id,
                    updateCriticalNotificationDto: input,
                },
            );
            await this.fetchCriticalNotifications();
            this.closeDialog();
        } catch (e) {
            runInAction(() => {
                this.createOrUpdateErrorMessage = e.message;
            });
            // eslint-disable-next-line no-console
            console.error(e);
        } finally {
            this.setLoading(false);
        }
    }

    async delete(id: string): Promise<void> {
        this.setDeleteInProgress(true);
        try {
            const deleted =
                await this.rootStore.apiClient.adminCriticalNotificationsController.criticalNotificationAdminControllerDelete(
                    { id },
                );

            if (deleted) {
                await this.fetchCriticalNotifications();
            }
        } catch (e) {
            // eslint-disable-next-line no-console
            console.error(e);
            this.rootStore.alertStore.setErrorMessageByStatus(e.message);
        } finally {
            this.setDeleteInProgress(false);
        }
    }

    openCreateDialog(): void {
        this.dialogMode = 'create';
        this.editedCriticalNotification = null;
        this.dialogOpened = true;
        this.rootStore.alertStore.setModalPlacement();
    }

    openUpdateDialog(criticalNotification: CriticalNotificationDto): void {
        this.dialogMode = 'update';
        this.editedCriticalNotification = criticalNotification;
        this.dialogOpened = true;
        this.rootStore.alertStore.setModalPlacement();
    }

    closeDialog(): void {
        this.dialogOpened = false;
        this.editedCriticalNotification = null;
        this.createOrUpdateErrorMessage = null;
        this.rootStore.alertStore.setDefaultPlacement();
    }

    setLoading(loading: boolean): void {
        this.loading = loading;
    }

    setDeleteInProgress(deleteInProgress: boolean): void {
        this.deleteInProgress = deleteInProgress;
    }

    get hasCriticalNotifications(): boolean {
        return this.criticalNotifications.length > 0;
    }

    get initialDialogValues(): CreateCriticalNotificationDto | UpdateCriticalNotificationDto {
        if (this.dialogMode === 'update' && this.editedCriticalNotification) {
            return {
                text: this.editedCriticalNotification.text,
                severity: this.editedCriticalNotification.severity,
                activeFrom: this.editedCriticalNotification.activeFrom,
                activeTo: this.editedCriticalNotification.activeTo,
            };
        }

        const activeFrom = new Date();
        const activeTo = new Date(activeFrom);
        activeTo.setDate(activeTo.getDate() + 1);

        return {
            text: '',
            severity: CriticalNotificationSeverityEnum.Info,
            activeFrom,
            activeTo,
        };
    }
}
